/** @format */

import { create } from "zustand";
import {
  feedbackService,
  type CreateFeedbackPayload,
  type FeedbackItem,
  type FeedbackListParams,
  type FeedbackStatus,
} from "../api/feedbackService";

const inFlightFeedbackByVersion = new Map<string, Promise<void>>();

interface FeedbackPagination {
  page: number;
  limit: number;
  total: number;
  hasNext: boolean;
}

interface FeedbackState {
  /** Keyed by `"${sopId}:${versionId}:${stepIndex}"` */
  feedbackByStep: Record<string, FeedbackItem[]>;
  /** All feedback for a version, keyed by `"${sopId}:${versionId}"`. Used by Suggestions tab. */
  feedbackByVersion: Record<string, FeedbackItem[]>;
  /** Single selected feedback ID per step key (radio). When set, only this one is highlighted; when null, all are shown. */
  selectedFeedbackIdByStep: Record<string, string | null>;
  /** Feedback IDs whose selection no longer matches current doc (orphaned). Keyed by step key. Used by Suggestions tab. */
  orphanedFeedbackIdsByStep: Record<string, string[]>;
  allFeedback: FeedbackItem[];
  pagination: FeedbackPagination | null;
  submitting: boolean;
  loading: boolean;

  setSelectedFeedbackIdForStep: (stepKey: string, feedbackId: string | null) => void;
  setOrphanedFeedbackIds: (stepKey: string, feedbackIds: string[]) => void;
  /** Fetch all feedback for a version (all steps) and populate feedbackByVersion + feedbackByStep. */
  fetchFeedbackForVersion: (sopId: string, versionId: string) => Promise<void>;

  submitFeedback: (
    sopId: string,
    payload: CreateFeedbackPayload,
  ) => Promise<void>;
  fetchFeedbackForStep: (
    sopId: string,
    versionId: string,
    stepIndex: number,
  ) => Promise<void>;
  fetchAllFeedback: (
    sopId?: string,
    params?: FeedbackListParams,
  ) => Promise<void>;
  updateFeedbackStatus: (
    sopId: string,
    feedbackId: string,
    status: FeedbackStatus,
  ) => Promise<void>;
  deleteFeedback: (sopId: string, feedbackId: string) => Promise<void>;
}

export const useFeedbackStore = create<FeedbackState>((set) => ({
  feedbackByStep: {},
  feedbackByVersion: {},
  selectedFeedbackIdByStep: {},
  orphanedFeedbackIdsByStep: {},
  allFeedback: [],
  pagination: null,
  submitting: false,
  loading: false,

  setOrphanedFeedbackIds: (stepKey, feedbackIds) => {
    set((state) => ({
      orphanedFeedbackIdsByStep: {
        ...state.orphanedFeedbackIdsByStep,
        [stepKey]: feedbackIds,
      },
    }));
  },

  setSelectedFeedbackIdForStep: (stepKey, feedbackId) => {
    set((state) => {
      const versionPrefix = stepKey.slice(0, stepKey.lastIndexOf(":"));
      const next = { ...state.selectedFeedbackIdByStep };
      for (const key of Object.keys(next)) {
        if (key.startsWith(versionPrefix + ":") && key !== stepKey) next[key] = null;
      }
      next[stepKey] = feedbackId;
      return { selectedFeedbackIdByStep: next };
    });
  },

  fetchFeedbackForVersion: async (sopId, versionId) => {
    const versionKey = `${sopId}:${versionId}`;
    const existingRequest = inFlightFeedbackByVersion.get(versionKey);
    if (existingRequest) {
      return existingRequest;
    }

    const requestPromise = (async () => {
      set({ loading: true });
      try {
        const result = await feedbackService.list(sopId, { versionId });
        const list = result.feedback ?? [];
        const byStep: Record<string, FeedbackItem[]> = {};
        for (const item of list) {
          const stepKey = `${sopId}:${versionId}:${item.stepIndex}`;
          if (!byStep[stepKey]) byStep[stepKey] = [];
          byStep[stepKey].push(item);
        }
        set((state) => ({
          feedbackByVersion: { ...state.feedbackByVersion, [versionKey]: list },
          feedbackByStep: { ...state.feedbackByStep, ...byStep },
        }));
      } finally {
        set({ loading: false });
        inFlightFeedbackByVersion.delete(versionKey);
      }
    })();

    inFlightFeedbackByVersion.set(versionKey, requestPromise);
    return requestPromise;
  },

  submitFeedback: async (sopId, payload) => {
    set({ submitting: true });
    try {
      await feedbackService.create(sopId, payload);
      const versionKey = `${sopId}:${payload.versionId}`;
      const stepKey = `${sopId}:${payload.versionId}:${payload.stepIndex}`;
      const mergeLists = (feedback: FeedbackItem[]) => {
        set((state) => {
          const prevVersion = state.feedbackByVersion[versionKey] ?? [];
          const rest = prevVersion.filter((f) => f.stepIndex !== payload.stepIndex);
          return {
            feedbackByStep: { ...state.feedbackByStep, [stepKey]: feedback },
            feedbackByVersion: {
              ...state.feedbackByVersion,
              [versionKey]: [...rest, ...feedback],
            },
          };
        });
      };
      const listParams = {
        versionId: payload.versionId,
        stepIndex: payload.stepIndex,
      };
      const updated = await feedbackService.list(sopId, listParams);
      mergeLists(updated.feedback ?? []);
      // Category is classified asynchronously; refresh once so the label updates after Bedrock.
      window.setTimeout(() => {
        void feedbackService.list(sopId, listParams).then((r) => mergeLists(r.feedback ?? []));
      }, 4000);
    } finally {
      set({ submitting: false });
    }
  },

  fetchFeedbackForStep: async (sopId, versionId, stepIndex) => {
    const key = `${sopId}:${versionId}:${stepIndex}`;
    set({ loading: true });
    try {
      const result = await feedbackService.list(sopId, {
        versionId,
        stepIndex,
      });
      set((state) => ({
        feedbackByStep: { ...state.feedbackByStep, [key]: result.feedback },
      }));
    } finally {
      set({ loading: false });
    }
  },

  fetchAllFeedback: async (_sopId, params) => {
    set({ loading: true });
    try {
      const result = await feedbackService.listAll(params);
      set({
        allFeedback: result.feedback ?? [],
        pagination: result.pagination,
      });
    } finally {
      set({ loading: false });
    }
  },

  updateFeedbackStatus: async (sopId, feedbackId, status) => {
    await feedbackService.updateStatus(sopId, feedbackId, status);
    set((state) => ({
      allFeedback: state.allFeedback.map((f) =>
        f.id === feedbackId ? { ...f, status } : f,
      ),
      feedbackByStep: Object.fromEntries(
        Object.entries(state.feedbackByStep).map(([key, items]) => [
          key,
          items.map((f) => (f.id === feedbackId ? { ...f, status } : f)),
        ]),
      ),
      feedbackByVersion: Object.fromEntries(
        Object.entries(state.feedbackByVersion).map(([key, items]) => [
          key,
          items.map((f) => (f.id === feedbackId ? { ...f, status } : f)),
        ]),
      ),
    }));
  },

  deleteFeedback: async (sopId, feedbackId) => {
    await feedbackService.delete(sopId, feedbackId);
    set((state) => ({
      allFeedback: state.allFeedback.filter((f) => f.id !== feedbackId),
      feedbackByStep: Object.fromEntries(
        Object.entries(state.feedbackByStep).map(([key, items]) => [
          key,
          items.filter((f) => f.id !== feedbackId),
        ]),
      ),
      feedbackByVersion: Object.fromEntries(
        Object.entries(state.feedbackByVersion).map(([key, items]) => [
          key,
          items.filter((f) => f.id !== feedbackId),
        ]),
      ),
    }));
  },
}));
